import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { formatRelativeTime } from '../../utils/relativeTime';
import './sandbox.css';

/**
 * The live "is it connected?" of one AI tool's setup, from the sandbox overview's `aiTools`
 * (`[{ kind, label, lastSeenAt }]`, recorded by the gateway when the tool opens an MCP session, so only after its
 * sign-in worked). 'checking' until the overview answered, then 'connected' (with the entry) or 'waiting'.
 */
export function connectionState(tool, sandbox) {
  if (!sandbox) { return { state: 'checking', entry: null }; }
  const entry = (sandbox.aiTools || []).find((seen) => tool.kinds.includes(seen.kind)) || null;
  return { state: entry ? 'connected' : 'waiting', entry };
}

/**
 * "Connected ✓ Claude Code, last call 1 min ago" or "Not connected yet", refreshed by its parent's polling while the
 * setup is open; "Check now" asks again. `hint` shows while it waits (what usually goes wrong for this tool).
 */
export default function ConnectionCheck({ tool, sandbox = null, onCheck = null, hint = null }) {
  const { state, entry } = connectionState(tool, sandbox);
  const when = entry?.lastSeenAt ? formatRelativeTime(entry.lastSeenAt) : '';
  return (
    <div className={`sbx-conn-check ${state === 'connected' ? 'ok' : 'wait'}`} role="status">
      <span aria-hidden="true" className="sbx-conn-dot" />
      <span className="sbx-conn-text">
        {state === 'checking' && <strong>Checking the connection…</strong>}
        {state === 'connected' && (
          <>
            <strong>{`Connected ✓ ${tool.name}`}</strong>
            <span>{when ? `Last call to your sandbox ${when === 'now' ? 'just now' : when}.` : 'It reached your sandbox.'}</span>
          </>
        )}
        {state === 'waiting' && (
          <>
            <strong>Not connected yet</strong>
            <span>{`This turns green after ${tool.name}'s first call to your sandbox. Checking every few seconds while this is open.`}</span>
            {hint && <span className="sbx-conn-hint">{hint}</span>}
          </>
        )}
      </span>
      {onCheck && state !== 'connected' && <Button onClick={onCheck} size="small">Check now</Button>}
    </div>
  );
}

ConnectionCheck.propTypes = {
  tool: PropTypes.shape({ name: PropTypes.string.isRequired, kinds: PropTypes.arrayOf(PropTypes.string).isRequired }).isRequired,
  sandbox: PropTypes.object,
  onCheck: PropTypes.func,
  hint: PropTypes.node,
};
